import type { ApolloCache } from "@apollo/client/cache";
import { useMutation, useQuery } from "@apollo/client/react";
import {
  DeleteListingDocument,
  ListingDocument,
  SetListingStateDocument,
  type ListingQuery,
  type ListingState,
} from "@/graphql/generated/operations";

/** Eine Ausschreibung, wie das Dashboard sie liest (Query `Listing`). */
export type ListingDetail = NonNullable<ListingQuery["listing"]>;

export interface UseListingResult {
  listing: ListingDetail | null;
  loading: boolean;
  error: Error | null;
  busy: boolean;
  /**
   * Wechselt in `target`. `deadline` geht nur mit, wenn er übergeben wird: weggelassen bleibt die Frist,
   * null entfernt sie (genesis SetListingStateInput).
   */
  transition: (target: ListingState, deadline?: string | null) => Promise<void>;
  remove: () => Promise<void>;
}

// Die Liste filtert nach Zustand und sortiert nach Anlage; nach einem Wechsel oder Löschen soll sie
// beim nächsten Öffnen neu fragen, statt einen alten Stand zu zeigen.
export function forgetListings(cache: ApolloCache) {
  cache.evict({ fieldName: "listings" });
  cache.gc();
}

// Nach einer Änderung der Zugriffsliste: auch die Bewerbungslisten, denn wer sie liest, entscheidet genesis neu
// (@external-adr GENESIS-0096-listing-applications-are-read-only-by-their-explicit-readers). Jede Liste wird beim
// nächsten Öffnen serverseitig erneut geprüft.
export function forgetListingAccess(cache: ApolloCache) {
  cache.evict({ fieldName: "listingApplications" });
  forgetListings(cache);
}

// Titelbild und weitere Bilder einer Ausschreibung aus dem Cache nehmen, nachdem sie geschrieben wurden:
// keine Antwort darauf trägt sie (das wären die Originale), also lesen Detail und Kacheln sie beim
// nächsten Öffnen neu. Ohne Rundruf, denn die Bearbeiten-Seite beobachtet dieselbe Ausschreibung noch
// und lüde sonst ihre Originale neu, kurz bevor sie geht.
export function forgetListingImages(cache: ApolloCache, listingId: string) {
  const id = cache.identify({ __typename: "Listing", id: listingId });
  cache.evict({ id, fieldName: "mainImage", broadcast: false });
  cache.evict({ id, fieldName: "images", broadcast: false });
}

export function useListing(listingId: string): UseListingResult {
  const query = useQuery(ListingDocument, { variables: { id: listingId }, skip: !listingId });
  const [setStateMutation, setStateStatus] = useMutation(SetListingStateDocument);
  const [deleteMutation, deleteStatus] = useMutation(DeleteListingDocument);

  async function transition(target: ListingState, deadline?: string | null) {
    await setStateMutation({
      variables: {
        input: {
          id: listingId,
          state: target,
          ...(deadline !== undefined ? { deadline } : {}),
          dryRun: false,
        },
      },
      update: forgetListings,
    });
  }

  async function remove() {
    await deleteMutation({
      variables: { input: { id: listingId, dryRun: false } },
      update(cache) {
        cache.evict({ id: cache.identify({ __typename: "Listing", id: listingId }) });
        forgetListings(cache);
      },
    });
  }

  const listing = query.data?.listing ?? null;
  return {
    listing,
    loading: query.loading && !listing,
    error: query.error ?? null,
    busy: setStateStatus.loading || deleteStatus.loading,
    transition,
    remove,
  };
}
